import type { LiftParts, Loadout, StarMetric, UpgradeSystem } from '@full-elevator/sim';
import { isBaseLoadout, shelf } from '@full-elevator/sim';
import type { LevelRecord } from './storage.js';

/**
 * The garage, taught (ADR-0041).
 *
 * Nothing pointed at the garage. Outside players never found it: one save went
 * from office shift 11 to the hotel without fitting a part, with about a
 * thousand coins in it (PLAYTEST_LOG, 2026-10-21). A bubble over Upgrade was
 * not enough either: the owner missed it (PLAYTEST_LOG, 2026-10-22). So the
 * first time the motor is affordable, the screen dims but for Upgrade, a hand
 * points at it, and the garage then lights the part to fit and waits for the
 * tap, the way the guided shifts teach the lift. After that, the results
 * screen suggests an upgrade where a player is stuck.
 *
 * It reads the save and the shelf and decides nothing about the game: the
 * purchase is the garage's ordinary one.
 */

/** Kept in the save's `coached` list with the guided shifts' lessons, once done. */
export const GARAGE_LESSON = 'garage/first-part';

/**
 * The part to fit first: the motor, which takes time off every trip and is
 * the one a player feels at once (ADR-0041). Null until the coins reach it.
 *
 * It used to fall back to the cheapest part that fitted. Since pay rises
 * through a building (ADR-0042), something is affordable at 41–47 coins and
 * the motor costs 60, so every player was taught the rope, the part that
 * changes a shift least (balance review, 2026-10-26). The owner chose the
 * motor, even a shift or two later (ADR-0047). A shelf with no motor on it at
 * all, which a lift with nothing fitted never has, still teaches its cheapest.
 */
export function firstPart(coins: number, loadout: Loadout, parts: LiftParts): UpgradeSystem | null {
  const offers = shelf({ coins, loadout }, parts);
  const motor = offers.find((offer) => offer.system === 'motor');
  if (motor) return motor.affordable ? 'motor' : null;
  return offers.find((offer) => offer.affordable)?.system ?? null;
}

/**
 * Due once, the first time the motor is in reach. A player who has fitted a
 * part on any lift found the garage on their own and is never taught it.
 */
export function garageLessonDue(
  coached: readonly string[],
  loadouts: Readonly<Record<string, Loadout>>,
  coins: number,
  loadout: Loadout,
  parts: LiftParts,
): boolean {
  if (coached.includes(GARAGE_LESSON)) return false;
  if (Object.values(loadouts).some((fitted) => !isBaseLoadout(fitted))) return false;
  return firstPart(coins, loadout, parts) !== null;
}

/** Where the lesson is: pointing at Upgrade, at the part's Fit, or done. */
export type LessonStep = 'call' | 'fit' | 'done';

export interface LessonWords {
  readonly kicker: string;
  readonly line: string;
  readonly todo: string;
  /** The card's own button, when there is nothing else to tap. */
  readonly button?: string;
}

/** What the lesson says at each step, about this part in this building. */
export function lessonWords(
  step: LessonStep,
  part: string,
  coins: number,
  building: string,
): LessonWords {
  const place = building.replace(/^The /, '');
  if (step === 'call') {
    return {
      kicker: 'Your first part',
      line: `${coins} coins: enough for the ${part}.`,
      todo: 'A better lift makes every shift easier. Tap Upgrade, and we will fit it together.',
    };
  }
  if (step === 'fit') {
    // The part's own card, lit beside this one, says what it does.
    return {
      kicker: 'Your first part',
      line: `The ${part}. Its card says what it does.`,
      todo: 'Tap Fit to put it on your lift.',
    };
  }
  return {
    kicker: 'Fitted!',
    line: `It is on the ${place}’s lift from your next shift.`,
    todo: 'Every building has a lift of its own. The next one starts with basic parts again, and you will fit its parts here too.',
    button: 'Got it',
  };
}

/**
 * A word on the results where a better lift would help, and one is in reach:
 * a win at one star, or three tries at a shift without a win. Never on a
 * puzzle, whose trips no lift changes.
 */
export function upgradeHint(
  run: { readonly stars: number; readonly metric: StarMetric },
  record: LevelRecord | undefined,
  affordable: boolean,
): string | null {
  if (!affordable || run.metric === 'trips') return null;
  if (run.stars === 1) return 'One star. A better lift would help here.';
  if (run.stars === 0 && (record?.stars ?? 0) === 0 && (record?.plays ?? 0) >= 3) {
    return 'Three tries. A better lift would help.';
  }
  return null;
}
